import * as React from 'react'
import { fetchDeployedVersion } from '@/helpers/versionClient'

/** How often to re-check for a new deploy while the tab is open. */
const POLL_INTERVAL_MS = 60_000

/**
 * True when the deployed web app (`/version.json`) is a different build than
 * this tab's JS (`__APP_VERSION_SHA__`, inlined at build time).
 *
 * Using the baked-in SHA as the baseline avoids a false "already current"
 * baseline when version.json has rolled forward before the user reloads.
 */
export function useAppUpdateAvailable(): boolean {
  const [updateAvailable, setUpdateAvailable] = React.useState(false)

  React.useEffect(() => {
    let canceled = false
    let intervalId: number | undefined

    async function check(): Promise<void> {
      if (canceled) return
      const version = await fetchDeployedVersion()
      if (canceled || !version) return
      if (version.sha === __APP_VERSION_SHA__) return

      setUpdateAvailable(true)
      if (intervalId !== undefined) {
        window.clearInterval(intervalId)
        intervalId = undefined
      }
    }

    void check()
    intervalId = window.setInterval(() => {
      void check()
    }, POLL_INTERVAL_MS)

    function onVisible(): void {
      if (document.visibilityState === 'visible') {
        void check()
      }
    }
    document.addEventListener('visibilitychange', onVisible)

    return () => {
      canceled = true
      if (intervalId !== undefined) {
        window.clearInterval(intervalId)
      }
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [])

  return updateAvailable
}
